// ── CardSuggest — поле «Перевод» с подсказками, как в Quizlet ────────────────
// Пока пишешь слово, под полем перевода появляются варианты (клик — вставить,
// Tab в пустом поле — вставить первый). Для японского — кана, её можно добавить
// к переводу. Язык перевода выбирается справа и запоминается.

import { useEffect, useId, useState } from 'react';
import { Chip } from './ui/Chip';
import { SANS } from './themes';
import { markSetting } from './settings';
import { suggestTranslation, getCardXl, CARD_XL_KEY } from './suggest';
import { detectTextLangByScript } from './utils';

import { t } from './i18n';
const XL = [['ru', t("русский")], ['en', t("английский")], ['uk', t("украинский")], ['de', t("немецкий")], ['fr', t("французский")],
  ['es', t("испанский")], ['it', t("итальянский")], ['pt', t("португальский")], ['pl', t("польский")], ['tr', t("турецкий")],
  ['ja', t("японский")], ['zh', t("китайский")], ['ko', t("корейский")]];
const SOURCE = { dict: t("общий словарь"), claude: 'Claude', server: t("ИИ-словарь"), mymemory: 'MyMemory' };
const KANA_KEY = 'ac_card_kana';

export default function CardTranslationField({ value, onChange, onEnter, word, lang: projLang, P, autoFocus }) {
  const inputId = useId();
  const [xl, setXl] = useState(getCardXl);
  const [sug, setSug] = useState(null);
  const [withKana, setWithKana] = useState(() => { try { return localStorage.getItem(KANA_KEY) !== '0'; } catch { return true; } });
  const w = String(word || '').trim();
  const lang = (projLang || detectTextLangByScript(w) || '').toLowerCase();

  useEffect(() => {
    if (!w || w.length > 80 || !lang || lang === xl) { setSug(null); return undefined; }
    const ctl = new AbortController();
    setSug((s) => (s && s.word === w ? s : { word: w, options: [], reading: '', loading: true }));
    const t = setTimeout(() => {
      suggestTranslation({ word: w, lang, xl, signal: ctl.signal, onUpdate: (r) => setSug({ ...r, word: w }) });
    }, 450);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [w, lang, xl]);

  const reading = sug?.reading || '';
  const compose = (opt) => (withKana && reading ? `${reading} — ${opt}` : opt);
  const options = sug?.options || [];

  const pickXl = (v) => { setXl(v); try { localStorage.setItem(CARD_XL_KEY, v); markSetting(CARD_XL_KEY); } catch {} };
  const toggleKana = () => {
    const next = !withKana; setWithKana(next);
    try { localStorage.setItem(KANA_KEY, next ? '1' : '0'); } catch {}
    // Если в поле уже стоит подсказка — переписать её с каной или без.
    const cur = String(value || '');
    const base = reading && cur.startsWith(`${reading} — `) ? cur.slice(reading.length + 3) : cur;
    if (options.includes(base)) onChange(next && reading ? `${reading} — ${base}` : base);
  };

  // Поле и подписи — как у общей детали Field (src/ui): высота 44, на телефоне шрифт 16.
  const phone = typeof window !== 'undefined' && window.innerWidth < 768;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 2, gap: '0 8px' }}>
        <label htmlFor={inputId} style={{ fontSize: 11, color: P.mid, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em' }}>{t("Перевод")}</label>
        <label title={t("Язык перевода")} style={{ fontSize: 11.5, color: P.faint, display: 'inline-flex', alignItems: 'center', gap: 4, minHeight: 32 }}>
          {lang ? lang.toUpperCase() : '?'} →
          <select value={xl} onChange={(e) => pickXl(e.target.value)} aria-label={t("Язык перевода")}
            style={{ border: 'none', background: 'transparent', color: P.blueDark || P.blue, fontWeight: 700, fontSize: 12, fontFamily: SANS, cursor: 'pointer',
              minHeight: 32, padding: '0 2px', outline: 'none' }}>
            {XL.map(([c, n]) => <option key={c} value={c}>{c.toUpperCase()} · {n}</option>)}
          </select>
        </label>
      </div>
      <input id={inputId} value={value || ''} autoFocus={autoFocus}
        placeholder={options[0] ? t("{0}   (Tab — вставить)",[compose(options[0])]) : t("напр. решать")}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Tab' && !e.shiftKey && !String(value || '').trim() && options[0]) { e.preventDefault(); onChange(compose(options[0])); }
          else if (e.key === 'Enter') onEnter?.();
        }}
        style={{ width: '100%', minHeight: 44, background: P.surface, border: `1.5px solid ${P.border}`, borderRadius: 10, color: P.text, padding: '0 13px',
          fontSize: phone ? 16 : 14, fontFamily: SANS, display: 'block', outline: 'none', boxSizing: 'border-box' }} />
      {w && lang && lang !== xl && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 8, minHeight: 32 }}>
          {options.map((o) => (
            <Chip key={o} C={P} selected={value === compose(o)} onClick={() => onChange(compose(o))} style={{ maxWidth: '100%' }}>{o}</Chip>
          ))}
          {reading && (
            <Chip C={P} selected={withKana} onClick={toggleKana} title={t("Добавлять чтение каной перед переводом")}>
              {withKana ? '✓ ' : '+ '}{t("кана:")} {reading}
            </Chip>
          )}
          {sug?.loading && !options.length && <span style={{ fontSize: 12, color: P.faint }}>{t("Ищу перевод…")}</span>}
          {!sug?.loading && !options.length && !reading && sug && <span style={{ fontSize: 12, color: P.faint }}>{t("Подсказок нет — впишите перевод сами")}</span>}
          {sug?.source && options.length > 0 && <span style={{ fontSize: 11, color: P.faint, marginLeft: 'auto' }}>{SOURCE[sug.source]}</span>}
        </div>
      )}
      {w && !lang && (
        <div style={{ fontSize: 12, color: P.faint, marginTop: 8 }}>{t("Укажите язык проекта, чтобы появились подсказки перевода.")}</div>
      )}
    </div>
  );
}
